import { useMemo } from 'react';
import { dwrClassesById } from '@/data/classes';
import { findMoveByIdGlobal } from '@/data/moves';
import { findSpellsByClass, findSpellsByClassAndTradition } from '@/data/spells';
import type { Character } from '@/schemas/character';
import type { Spell } from '@/schemas/spell';
import { type ActiveSpellGrant } from '@/services/spellGrants';
import { type ActiveSpellList } from '@/services/spellAccess';
import { getEffectiveCharacterLevelForOwnClass } from '@/services/multiclass';
import { getSpellPoolForGrantInContext } from '@/services/spellPoolDisplay';
import { SpellPickPanel } from '../../sections/SpellPickPanel';

// page-specific (Fase 2 PR 10 — decisione B1): sub-componenti di
// `LevelUpStepSpells` estratti per rispettare `max-lines: 400` nel file
// principale. Pattern coerente con `LevelUpStepMulticlassParts.tsx` PR 9.
//
// **PR-A2 (FI-36)**: `InteractiveGrantSection` ora consuma il
// `<SpellPickPanel>` shared (stesso pannello del builder L1), eliminando la
// duplicazione UI tra i 2 contesti. `TraditionInfoSection` resta separata —
// è una vista read-only "informativa" sulle spell auto della Tradizione
// nel level-up (non lega una grant attiva, è discoverabilità).

interface InteractiveGrantSectionProps {
  grant: ActiveSpellGrant;
  character: Character;
  /**
   * Livello che il PG sta raggiungendo in questo level-up (D3 fix).
   * Usato per il filtro `lte-character-level` di `getSpellPoolForGrant`.
   */
  targetLevel: number;
  selectedIds: readonly string[];
  /**
   * Spell ID off-limits per il pool: include `character.spells` + selezioni
   * nelle ALTRE sezioni (UI polish 2026-05-13). NON include la sezione corrente.
   */
  offLimitsIds: ReadonlySet<string>;
  onChange: (next: string[]) => void;
}

export function InteractiveGrantSection({
  grant,
  character,
  targetLevel,
  selectedIds,
  offLimitsIds,
  onChange,
}: InteractiveGrantSectionProps) {
  const move = useMemo(() => findMoveName(grant.moveId), [grant.moveId]);
  const ownClassId = useMemo(
    () => resolveOwnClassId(grant.moveId, character.classId),
    [grant.moveId, character.classId],
  );
  // **FI-57**: per le grant attivate da mosse acquisite via multiclasse, il
  // pool deve filtrare per il livello effettivo della **classe-source** della
  // mossa (es. Mago via Combattente Multiclasse a delta -1), non per il
  // `targetLevel` complessivo del PG. Per la classe primaria il valore
  // coincide con `targetLevel` (helper no-op).
  const effectiveCasterLevel = useMemo(
    () => getEffectiveCharacterLevelForOwnClass(character, ownClassId, targetLevel),
    [character, ownClassId, targetLevel],
  );
  const pool = useMemo(
    () =>
      getSpellPoolForGrantInContext(grant, {
        moves: character.moves,
        classId: character.classId,
        archetypeId: character.archetypeId,
        characterLevel: effectiveCasterLevel,
        alreadyKnownSpellIds: offLimitsIds,
      }),
    [
      grant,
      character.moves,
      character.classId,
      character.archetypeId,
      effectiveCasterLevel,
      offLimitsIds,
    ],
  );

  return (
    <SpellPickPanel
      grant={grant}
      pool={pool}
      selectedIds={selectedIds}
      onChange={onChange}
      headerText={describeGrantHeader(grant.grant, move)}
    />
  );
}

/**
 * **FI-56 (PR-A2 Step 6)** — Mostra una grant "auto" (`all-of-level` /
 * `fixed-list`) acquisita via mossa appena pescata nel level-up (tipico:
 * trucchetti Mago L0 auto via *Lanciare Incantesimi Arcani* pescata da
 * Guerriero multiclasse → bundle Corpus Arcano + Mana + LIA → grant
 * fixed-list 3 trucchetti). Read-only: il `<SpellPickPanel>` riconosce
 * `isAutoSpellGrantLevelRule` e renderizza senza checkbox.
 *
 * Coerente con il pattern del builder L1: stesso `<SpellPickPanel>` SSOT.
 */
interface AutoGrantInfoSectionProps {
  grant: ActiveSpellGrant;
  character: Character;
  targetLevel: number;
}

export function AutoGrantInfoSection({ grant, character, targetLevel }: AutoGrantInfoSectionProps) {
  const moveName = useMemo(() => findMoveName(grant.moveId), [grant.moveId]);
  const ownClassId = useMemo(
    () => resolveOwnClassId(grant.moveId, character.classId),
    [grant.moveId, character.classId],
  );
  const pool = useMemo(
    () =>
      getSpellPoolForGrantInContext(grant, {
        moves: character.moves,
        classId: character.classId,
        archetypeId: character.archetypeId,
        characterLevel: targetLevel,
      }),
    [grant, character.moves, character.classId, character.archetypeId, targetLevel],
  );

  return (
    <SpellPickPanel
      grant={grant}
      pool={pool}
      selectedIds={[]}
      onChange={noopOnChange}
      headerText={describeAutoGrantHeader(grant, moveName, ownClassId)}
    />
  );
}

function describeAutoGrantHeader(
  grant: ActiveSpellGrant,
  moveName: string,
  _ownClassId: Character['classId'],
): string {
  const levelRule = grant.grant.levelRule;
  if (levelRule.kind === 'fixed-list') {
    return `Trucchetti auto da «${moveName}»`;
  }
  if (levelRule.kind === 'all-of-level') {
    return `Incantesimi di livello ${levelRule.level} concessi da «${moveName}»`;
  }
  return `Incantesimi auto da «${moveName}»`;
}

interface TraditionInfoSectionProps {
  list: ActiveSpellList;
  characterLevel: number;
  targetLevel: number;
}

export function TraditionInfoSection({
  list,
  characterLevel,
  targetLevel,
}: TraditionInfoSectionProps) {
  const pool = useMemo(() => collectTraditionSpells(list, targetLevel), [list, targetLevel]);
  const header = describeTraditionHeader(list, pool.length, targetLevel);

  // Costruiamo una `ActiveSpellGrant` sintetica per riusare il `<SpellPickPanel>`
  // anche per la sezione informativa. La grant `all-of-level` indica al pannello
  // di renderizzare in mode `auto`. Usiamo `level: 0` arbitrariamente: il pool
  // è pre-calcolato (multi-livello), quindi il `level` della grant non
  // influenza il rendering.
  const syntheticGrant: ActiveSpellGrant = useMemo(
    () => ({
      moveId: `tradition-info:${list.classId}`,
      grantIndex: 0,
      grant: {
        trigger: 'on-take',
        levelRule: { kind: 'all-of-level', level: 0 },
        source: { kind: 'own-class' },
      },
    }),
    [list.classId],
  );

  return (
    <SpellPickPanel
      grant={syntheticGrant}
      pool={pool}
      selectedIds={[]}
      onChange={noopOnChange}
      headerText={header}
      characterLevelBefore={characterLevel}
      targetLevel={targetLevel}
      inputIdPrefix={`tradition-info-${list.classId}`}
    />
  );
}

// `TraditionInfoSection` è read-only: il `<SpellPickPanel>` riceve onChange
// per coerenza di firma ma non viene mai invocato (grant `all-of-level` =
// auto, niente checkbox). Funzione modulo-level per evitare la creazione
// di una closure ad ogni render.
function noopOnChange(): void {
  // intentionally empty
}

function describeGrantHeader(grant: ActiveSpellGrant['grant'], moveName: string): string {
  const { count, levelRule, source } = grant;
  const sourceText = describeSourceKind(source.kind);
  const levelText = describeLevelRule(levelRule);
  return `Da «${moveName}»: scegli ${count} incantesim${count === 1 ? 'o' : 'i'} ${sourceText} ${levelText}.`;
}

function describeSourceKind(kind: ActiveSpellGrant['grant']['source']['kind']): string {
  if (kind === 'own-class') return 'dalla lista della tua classe';
  if (kind === 'other-classes') return 'da qualsiasi altra classe';
  return 'da qualunque lista di classe';
}

function describeLevelRule(levelRule: ActiveSpellGrant['grant']['levelRule']): string {
  if (levelRule.kind === 'fixed') return `di livello ${levelRule.level}`;
  if (levelRule.kind === 'lte-character-level')
    return 'di livello pari o inferiore al tuo livello-classe';
  if (levelRule.kind === 'any-with-cap')
    return levelRule.maxLevel ? `fino al livello ${levelRule.maxLevel}` : 'di qualsiasi livello';
  if (levelRule.kind === 'all-of-level') return `(tutte di livello ${levelRule.level})`;
  return `(lista predefinita di ${levelRule.spellIds.length} incantesimi)`;
}

function describeTraditionHeader(
  list: ActiveSpellList,
  count: number,
  targetLevel: number,
): string {
  if (list.classId === 'chierico' || list.classId === 'paladino') {
    return `Tradizione Sacra «${list.tradizione}» — ${count} incantesimi disponibili a L${targetLevel}`;
  }
  return `Lista incantesimi ${classDisplayName(list.classId)} — ${count} a L${targetLevel}`;
}

// Lookup cross-class safe per il nome della mossa che ha attivato la grant.
// Quando una mossa è acquisita via multiclass bundle (es. Mago Corpus Arcano
// preso dal Guerriero), `grant.moveId` è 'mago.corpus-arcano' ma la classe
// del PG è 'guerriero': `findMoveByIdAnyScope('guerriero', 'mago.*')` non
// trova nulla → fallback alla string id. Soluzione: `findMoveByIdGlobal`
// cerca in tutti i 12 cataloghi (cross-class safe).
function findMoveName(moveId: string): string {
  const move = findMoveByIdGlobal(moveId);
  return move?.name ?? moveId;
}

function resolveOwnClassId(
  moveId: string,
  characterClassId: Character['classId'],
): Character['classId'] {
  const prefix = moveId.split('.')[0];
  if (!prefix) return characterClassId;
  if (prefix in dwrClassesById) return prefix as Character['classId'];
  return characterClassId;
}

function collectTraditionSpells(list: ActiveSpellList, maxLevel: number): readonly Spell[] {
  const all =
    list.classId === 'chierico' || list.classId === 'paladino'
      ? findSpellsByClassAndTradition(list.classId, list.tradizione)
      : findSpellsByClass(list.classId);
  return all.filter((s) => s.level <= maxLevel).sort((a, b) => a.level - b.level);
}

function classDisplayName(classId: ActiveSpellList['classId']): string {
  return dwrClassesById[classId]?.name ?? classId;
}
